import { OrganizationDto } from './../@types/dto/organization-dto.model';
import { inject, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FeatureService } from '@services/features/feature.service';
import { StorageService } from '@services/storage/storage.service';
import { AuthenticationService } from 'app/auth/service';
import { ConfigService } from 'app/app-config.service';
import { NeedHelpModalComponent } from './need-help-modal/need-help-modal.component';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { OrganizationState } from '@state/organization.state';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { combineLatest, Observable } from 'rxjs';
import { DcarMenuitem } from 'app/@types/shared/dcar-menuitem';
import { DCarbonizeAppConfig } from 'app/@types/shared/app-config';
import { ChangeOrganizationModalComponent } from './change-organization-modal/change-organization-modal.component';
import { UserHttpService } from '@services/datasources/user-http.service';
import { style } from '@angular/animations';

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html',
    styleUrls: ['./app.menu.component.scss'],
    providers: [DialogService],
})
export class AppMenuComponent implements OnInit {
    model: DcarMenuitem[] = [];

    modal: DynamicDialogRef | undefined;
    public organizationPricingPlan: OrganizationPricingPlanDto | undefined;
    @Select(OrganizationState.getOrganizationPricingPlan)
    organizationPricingPlan$: Observable<OrganizationPricingPlanDto>;
    @Select(OrganizationState.getOrganization) organization$: Observable<OrganizationDto>;

    public normalUser: boolean;
    private config: DCarbonizeAppConfig = this.configService.getConfig;
    public isLoading = false;
    constructor(
        private authenticationService: AuthenticationService,
        private configService: ConfigService,
        private dialogService: DialogService,
        private featureService: FeatureService,
        private translateService: CustomTranslateService,
        private adminSignals: AdminDataSyncService,
        private readonly userService: UserHttpService,
    ) {}

    ngOnInit() {
        combineLatest([this.organizationPricingPlan$, this.featureService.UserReadiness]).subscribe({
            next: ([orgPP, ready]) => {
                this.organizationPricingPlan = orgPP;
                if (ready) {
                    this.initMenu();
                }
            },
        });

        this.translateService.onLangChange.subscribe(() => {
            this.initMenu();
        });

        this.organizationPricingPlan$.subscribe((pp) => {
            this.organizationPricingPlan = pp;
            this.initMenu();
        });
    }

    private initMenu(): void {
        this.isLoading = true;
        combineLatest([this.organization$, this.userService.getAvailableOrganization()]).subscribe(
            ([org, avalaibleOrg]) => {
                const organization = org;
                const availableOrganization = avalaibleOrg as OrganizationDto[];
                this.model = [
                    {
                        items: [
                            {
                                label: this.translateService.instant('menu.app.measure'),
                                icon: 'square_foot',
                                routerLink: ['/app/measure'],
                                visible: this.hasUiFeature(['organization']) || this.isSudo(),
                                routerLinkActiveOptions: { exact: false },
                                disabled: !this.featureService.isOnboarded(),
                            },
                            {
                                label: this.translateService.instant('menu.app.results'),
                                icon: 'search_insights',
                                visible: this.hasUiFeature(['organization']) || this.isSudo(),
                                routerLink: ['/app/results/dimension'],
                                routerLinkActiveOptions: { exact: false },
                                disabled: !this.featureService.isOnboarded(),
                            },
                            {
                                label: this.translateService.instant('menu.app.solutions'),
                                icon: 'lightbulb',
                                routerLink: ['/app/solutions'],
                                routerLinkActiveOptions: { exact: false },
                                visible: this.hasUiFeature(['organization']) || this.isSudo(),
                                disabled: !this.featureService.isOnboarded(),
                            },
                            {
                                label: this.translateService.instant('menu.app.actionPlan'),
                                icon: 'checklist',
                                routerLink: ['/app/actionplan'],
                                visible: this.hasUiFeature(['organization']) || this.isSudo(),
                                disabled: !this.featureService.isOnboarded(),
                            },
                        ],
                    },
                    {
                        separator: true,
                        visible: this.hasUiFeature(['admin_admin', 'organization_admin'], false),
                    },
                    {
                        items: [
                            {
                                label: this.translateService.instant('menu.dedicatedAdmin.home'),
                                icon: 'admin_panel_settings',
                                routerLink: ['/dedicated_admin/home'],
                                visible:
                                    this.hasUiFeature(['admin_admin'], false) ||
                                    this.hasUiFeature(['admin_admin'], true) ||
                                    this.hasUiFeature(['organization_admin'], false) ||
                                    this.hasUiFeature(['organization_admin'], true),
                                disabled: !this.hasUiFeature(['organization_admin'], false) && !this.isSudo(),
                                command: () => {
                                    this.adminSignals.resetSelection();
                                },
                            },
                            {
                                label: this.translateService.instant('menu.admin.home'),
                                icon: 'shield_person',
                                routerLink: ['/admin/home'],
                                visible:
                                    this.hasUiFeature(['admin_admin'], false) ||
                                    this.hasUiFeature(['admin_admin'], true),
                                disabled:
                                    !this.hasUiFeature(['admin_admin'], false) &&
                                    !this.isSudo() &&
                                    !this.hasUiFeature(['admin_admin'], true),
                                command: () => {
                                    this.adminSignals.resetSelection();
                                },
                            },
                            {
                                label: 'Dashboard',
                                icon: 'token',
                                routerLink: ['/dedicated_admin/dashboard'],
                                visible:
                                    (this.hasUiFeature(['organization_admin'], false) &&
                                        !this.hasUiFeature(['admin_admin'], false)) ||
                                    (this.hasUiFeature(['organization_admin'], true) &&
                                        !this.hasUiFeature(['admin_admin'], true)),
                                disabled: !this.hasUiFeature(['organization_admin'], false) && !this.isSudo(),
                                command: () => {
                                    this.adminSignals.resetSelection();
                                },
                            },
                        ],
                    },
                    {
                        separator: true,
                    },
                    {
                        items: [
                            {
                                isFreeTrialItem: true,
                                visible:
                                    this.organizationPricingPlan !== undefined &&
                                    this.organizationPricingPlan.pricingPlanId ===
                                        'bc1b9506-260b-4b3c-b680-126505a307cb' &&
                                    this.config.client.name === 'Dcarbonize',
                                styleClass: 'menu-item-margin-bottom',
                            },
                        ],
                    },
                    {
                        items: [
                            {
                                isMenuItem: true,
                            },
                            {
                                label: this.translateService.instant('menu.actions.help'),
                                icon: 'contact_support',
                                command: () => {
                                    this.dialogService.open(NeedHelpModalComponent, {
                                        modal: true,
                                        dismissableMask: true,
                                        autoZIndex: true,
                                        closable: true,
                                        closeOnEscape: true,
                                        showHeader: true,
                                        transitionOptions: '300ms cubic-bezier(0, 0, 0.2, 1)',
                                        styleClass: 'need-help-dialog',
                                    });
                                },
                            },
                            {
                                label: this.translateService.instant('menu.actions.settings'),
                                icon: 'settings',
                                routerLink: [
                                    this.featureService.hasUiFeatures(['admin_admin'], false)
                                        ? '/admin/settings'
                                        : this.featureService.hasUiFeatures(['organization_admin'], false)
                                        ? 'dedicated_admin/settings'
                                        : '/app/settings',
                                ],
                                routerLinkActiveOptions: { exact: false },
                                disabled:
                                    !this.featureService.isOnboarded() &&
                                    !this.featureService.hasUiFeatures(['organization_admin'], false),
                            },
                            ...(availableOrganization.length > 1
                                ? [
                                      {
                                          label: this.translateService.instant('components.changeOrg.menu'),
                                          icon: 'sync_alt',
                                          routerLink: ['/app/change-organization'],
                                          visible: this.hasUiFeature(['organization']),
                                      },
                                  ]
                                : []),
                            {
                                label: this.translateService.instant('menu.actions.logout'),
                                icon: 'logout',
                                command: () => {
                                    this.authenticationService.logout().subscribe();
                                },
                            },
                        ],
                    },
                ];
                this.isLoading = false;
            },
            () => (this.isLoading = true),
            () => {
                this.isLoading = true;
            },
        );
    }

    hasUiFeature(uiFeatures: string[], forOrganization = true): boolean {
        return this.featureService.hasUiFeatures(uiFeatures, forOrganization);
    }

    isSudo(): boolean {
        return this.featureService.hasSudoFeature();
    }
}
